import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import {
  Search, Wrench, Store, Shield, BarChart3, CreditCard,
  Users, Zap, Check, ChevronRight, ArrowRight, MapPin,
  Phone, Star, X, Eye, EyeOff, Building2, Clock, CheckCircle, Package
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─────── Color tokens ─────── */
const GY_GREEN = '#009b3a';
const GY_YELLOW = '#fedd00';
const GY_RED   = '#ef3340';

/* ─────── Landing Page ─────── */
const GuyarepairLandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [plans, setPlans] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [shops, setShops] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTotal, setSearchTotal] = useState(0);
  const [showRegister, setShowRegister] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('pro');
  // Repair lookup state
  const [repairRef, setRepairRef] = useState('');
  const [repairResult, setRepairResult] = useState(null);
  const [repairError, setRepairError] = useState('');
  const [repairLoading, setRepairLoading] = useState(false);

  // Map backend FR status values to localized labels (logic untouched, display only)
  const statutLabel = (s) => {
    const map = {
      'Terminé': t('guyarepairLanding.statutTermine'),
      'En cours': t('guyarepairLanding.statutEnCours'),
      'En attente': t('guyarepairLanding.statutEnAttente'),
    };
    return map[s] || s || t('guyarepairLanding.statutEnAttente');
  };

  useEffect(() => {
    axios.get(`${API}/api/public/plans`).then(r => setPlans(r.data.plans || {})).catch(() => {});
  }, []);

  const doSearch = useCallback(async (q) => {
    setSearchLoading(true);
    try {
      const res = await axios.get(`${API}/api/public/shops`, { params: { q, limit: 12 } });
      setShops(res.data.shops || []);
      setSearchTotal(res.data.total || 0);
    } catch { setShops([]); }
    finally { setSearchLoading(false); }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    doSearch(searchQuery);
  };

  const handleRepairLookup = async (e) => {
    e.preventDefault();
    if (!repairRef.trim()) return;
    setRepairError('');
    setRepairResult(null);
    setRepairLoading(true);
    try {
      const res = await axios.get(`${API}/api/public/repair-lookup`, { params: { ref: repairRef.trim() } });
      setRepairResult(res.data);
    } catch (err) {
      setRepairError(err.response?.data?.detail || t('guyarepairLanding.repairErrorDefault'));
    }
    setRepairLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#050a14] text-white" data-testid="guyarepair-landing">
      {/* ═══ Navbar ═══ */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-[#050a14]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/guyarepair_logo.png" alt="GUYAREPAIR" className="w-9 h-9 rounded-lg object-contain" />
            <span className="text-lg font-black tracking-wide">GUYA<span style={{color: GY_GREEN}}>RE</span><span style={{color: GY_YELLOW}}>PA</span><span style={{color: GY_RED}}>IR</span></span>
          </div>
          <div className="flex items-center gap-3">
            <a href="#pricing" className="hidden sm:inline text-sm text-gray-400 hover:text-white transition-colors px-3 py-1.5">{t('guyarepairLanding.navTarifs')}</a>
            <a href="#directory" className="hidden sm:inline text-sm text-gray-400 hover:text-white transition-colors px-3 py-1.5">{t('guyarepairLanding.navAnnuaire')}</a>
            <a href="#repair-lookup" className="hidden sm:inline text-sm text-gray-400 hover:text-white transition-colors px-3 py-1.5">{t('guyarepairLanding.navMaReparation')}</a>
            <button onClick={() => navigate('/guyarepair/connexion')} data-testid="nav-login-btn" className="text-sm text-gray-300 px-4 py-2 rounded-lg border border-white/10 hover:border-white/20 hover:bg-white/5 transition-all">
              {t('guyarepairLanding.navConnexion')}
            </button>
            <button onClick={() => { setSelectedPlan('pro'); setShowRegister(true); }} data-testid="nav-register-btn" className="text-sm font-semibold px-4 py-2 rounded-lg text-[#050a14] transition-all" style={{background: `linear-gradient(135deg, ${GY_GREEN}, ${GY_YELLOW})`}}>
              {t('guyarepairLanding.navEssaiGratuit')}
            </button>
          </div>
        </div>
      </nav>

      {/* ═══ Hero ═══ */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{background: `radial-gradient(ellipse 60% 50% at 50% 0%, ${GY_GREEN}33, transparent)`}} />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <img src="/guyarepair_logo.png" alt="GUYAREPAIR" className="w-24 h-24 mx-auto mb-4 object-contain" />
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-gray-400 mb-6">
            <Zap className="w-3 h-3" style={{color: GY_YELLOW}} /> {t('guyarepairLanding.heroBadge')}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight mb-6">
            {t('guyarepairLanding.heroTitleLine1')}<br/>{t('guyarepairLanding.heroTitleLine2')} <span style={{color: GY_GREEN}}>{t('guyarepairLanding.heroTitleHighlight')}</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            {t('guyarepairLanding.heroSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={() => { setSelectedPlan('pro'); setShowRegister(true); }} data-testid="hero-cta-btn" className="flex items-center gap-2 px-7 py-3.5 rounded-xl font-bold text-[#050a14] text-sm transition-all hover:scale-[1.02]" style={{background: `linear-gradient(135deg, ${GY_GREEN}, ${GY_YELLOW})`}}>
              {t('guyarepairLanding.heroCtaStart')} <ArrowRight className="w-4 h-4" />
            </button>
            <a href="#pricing" className="flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm text-gray-300 border border-white/10 hover:border-white/20 hover:bg-white/5 transition-all">
              {t('guyarepairLanding.heroCtaPricing')} <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ═══ Repair Lookup ═══ */}
      <section id="repair-lookup" className="py-16 px-4 relative">
        <div className="absolute inset-0 opacity-10" style={{background: `radial-gradient(ellipse 40% 50% at 50% 50%, ${GY_YELLOW}33, transparent)`}} />
        <div className="max-w-xl mx-auto relative z-10">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-3" style={{background: `${GY_YELLOW}15`, border: `1px solid ${GY_YELLOW}20`}}>
              <Search className="w-6 h-6" style={{color: GY_YELLOW}} />
            </div>
            <h2 className="text-lg font-bold text-white mb-1">{t('guyarepairLanding.repairLookupTitle')}</h2>
            <p className="text-sm text-gray-500">{t('guyarepairLanding.repairLookupSubtitle')}</p>
          </div>
          <form onSubmit={handleRepairLookup} className="flex gap-2 mb-4">
            <div className="flex-1 relative">
              <input
                type="text"
                value={repairRef}
                onChange={e => setRepairRef(e.target.value.toUpperCase())}
                placeholder={t('guyarepairLanding.repairLookupPlaceholder')}
                data-testid="repair-lookup-input"
                className="w-full px-4 py-3.5 bg-[#111827] border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#fedd00]/40 transition-colors font-mono tracking-wider"
              />
            </div>
            <button type="submit" disabled={repairLoading} data-testid="repair-lookup-btn" className="px-6 py-3.5 rounded-xl font-semibold text-sm text-[#050a14] transition-all hover:opacity-90 disabled:opacity-50" style={{background: GY_YELLOW}}>
              {repairLoading ? '...' : t('guyarepairLanding.rechercher')}
            </button>
          </form>

          {repairError && (
            <div className="p-3 rounded-lg text-xs text-center" data-testid="repair-lookup-error"
              style={{background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171'}}>
              {repairError}
            </div>
          )}

          {repairResult && !repairResult.found && (
            <div className="p-4 rounded-xl text-center" data-testid="repair-lookup-not-found"
              style={{background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)'}}>
              <p className="text-sm text-gray-400">{repairResult.message}</p>
            </div>
          )}

          {repairResult && repairResult.found && (
            <div className="rounded-xl overflow-hidden" data-testid="repair-lookup-found"
              style={{background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)'}}>
              <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
                <div>
                  <span className="text-[10px] uppercase tracking-wider text-gray-500">{t('guyarepairLanding.repairFoundAt')}</span>
                  <p className="text-sm font-bold text-white">{repairResult.company.name}</p>
                  {repairResult.company.city && <p className="text-[11px] text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3"/>{repairResult.company.city}</p>}
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold" style={{
                  background: repairResult.repair.statut === 'Terminé' ? 'rgba(16,185,129,0.15)' : repairResult.repair.statut === 'En cours' ? 'rgba(59,130,246,0.15)' : 'rgba(245,158,11,0.15)',
                  color: repairResult.repair.statut === 'Terminé' ? '#10b981' : repairResult.repair.statut === 'En cours' ? '#3b82f6' : '#f59e0b'
                }}>
                  {repairResult.repair.statut === 'Terminé' ? <CheckCircle className="w-3.5 h-3.5"/> : repairResult.repair.statut === 'En cours' ? <Wrench className="w-3.5 h-3.5"/> : <Clock className="w-3.5 h-3.5"/>}
                  {statutLabel(repairResult.repair.statut)}
                </div>
              </div>
              <div className="px-5 py-4 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('guyarepairLanding.labelReference')}</span>
                  <span className="text-white font-mono font-bold">{repairResult.repair.ref || repairResult.repair.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('guyarepairLanding.labelAppareil')}</span>
                  <span className="text-white">{repairResult.repair.marque} {repairResult.repair.modele}</span>
                </div>
                {repairResult.repair.probleme && <div className="flex justify-between"><span className="text-gray-500">{t('guyarepairLanding.labelProbleme')}</span><span className="text-white max-w-[200px] text-right">{repairResult.repair.probleme}</span></div>}
              </div>
              {repairResult.company.slug && (
                <div className="px-5 py-3 border-t border-white/5">
                  <button onClick={() => navigate(`/r/${repairResult.company.slug}`)} data-testid="repair-goto-company"
                    className="w-full py-2.5 rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-2"
                    style={{background: `${GY_GREEN}12`, border: `1px solid ${GY_GREEN}30`, color: GY_GREEN}}>
                    <Store className="w-3.5 h-3.5"/> {t('guyarepairLanding.voirPageDe', { nom: repairResult.company.name })} <ChevronRight className="w-3.5 h-3.5"/>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ═══ Search Directory ═══ */}
      <section id="directory" className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-lg font-bold text-white mb-2">{t('guyarepairLanding.directoryTitle')}</h2>
            <p className="text-sm text-gray-500">{t('guyarepairLanding.directorySubtitle')}</p>
          </div>
          <form onSubmit={handleSearch} className="flex gap-2 max-w-xl mx-auto mb-8">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('guyarepairLanding.searchPlaceholder')}
                data-testid="shop-search-input"
                className="w-full pl-12 pr-4 py-3.5 bg-[#111827] border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#009b3a]/40 transition-colors"
              />
            </div>
            <button type="submit" data-testid="shop-search-btn" className="px-6 py-3.5 rounded-xl font-semibold text-sm text-[#050a14] transition-all hover:opacity-90" style={{background: GY_GREEN}}>
              {t('guyarepairLanding.rechercher')}
            </button>
          </form>

          {/* Results */}
          {(shops.length > 0 || searchLoading) && (
            <div className="bg-[#111827] border border-white/5 rounded-2xl overflow-hidden" data-testid="shop-results">
              {searchLoading ? (
                <div className="p-8 text-center">
                  <div className="w-6 h-6 border-2 border-[#009b3a] border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : (
                <>
                  <div className="px-5 py-3 border-b border-white/5 text-xs text-gray-500">{t('guyarepairLanding.resultats', { count: searchTotal })}</div>
                  <div className="divide-y divide-white/5">
                    {shops.map(shop => (
                      <div key={shop.id} className="px-5 py-4 flex items-center gap-4 hover:bg-white/[0.02] transition-colors cursor-pointer" data-testid={`shop-${shop.id}`} onClick={() => navigate(shop.slug ? `/r/${shop.slug}` : `/shop/${shop.id}`)}>
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{background: `${GY_GREEN}15`}}>
                          <Store className="w-6 h-6" style={{color: GY_GREEN}} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-white">{shop.name}</h3>
                          <div className="flex items-center gap-3 mt-0.5">
                            {shop.city && <span className="flex items-center gap-1 text-xs text-gray-500"><MapPin className="w-3 h-3" />{shop.city}</span>}
                            {shop.phone && <span className="flex items-center gap-1 text-xs text-gray-500"><Phone className="w-3 h-3" />{shop.phone}</span>}
                          </div>
                          {shop.description && <p className="text-xs text-gray-600 mt-1 truncate">{shop.description}</p>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-0.5 rounded-full font-bold uppercase" style={{background: `${GY_GREEN}15`, color: GY_GREEN}}>{shop.subscription_plan}</span>
                          <ChevronRight className="w-4 h-4 text-gray-600" />
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ═══ Features ═══ */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-lg font-bold text-white mb-2">{t('guyarepairLanding.featuresTitle')}</h2>
            <p className="text-sm text-gray-500">{t('guyarepairLanding.featuresSubtitle')}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Wrench, title: t('guyarepairLanding.feature1Title'), desc: t('guyarepairLanding.feature1Desc'), color: GY_GREEN },
              { icon: CreditCard, title: t('guyarepairLanding.feature2Title'), desc: t('guyarepairLanding.feature2Desc'), color: GY_YELLOW },
              { icon: BarChart3, title: t('guyarepairLanding.feature3Title'), desc: t('guyarepairLanding.feature3Desc'), color: '#3b82f6' },
              { icon: Users, title: t('guyarepairLanding.feature4Title'), desc: t('guyarepairLanding.feature4Desc'), color: GY_RED },
              { icon: Shield, title: t('guyarepairLanding.feature5Title'), desc: t('guyarepairLanding.feature5Desc'), color: '#8b5cf6' },
              { icon: Store, title: t('guyarepairLanding.feature6Title'), desc: t('guyarepairLanding.feature6Desc'), color: '#f97316' },
            ].map((f, i) => (
              <div key={i} className="bg-[#111827] border border-white/5 rounded-xl p-5 hover:border-white/10 transition-all group">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{background: `${f.color}15`}}>
                  <f.icon className="w-5 h-5" style={{color: f.color}} />
                </div>
                <h3 className="text-sm font-bold text-white mb-1">{f.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Pricing ═══ */}
      <section id="pricing" className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-lg font-bold text-white mb-2">{t('guyarepairLanding.pricingTitle')}</h2>
            <p className="text-sm text-gray-500">{t('guyarepairLanding.pricingSubtitle')}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {Object.entries(plans).map(([key, plan]) => {
              const isHighlight = plan.highlight;
              return (
                <div
                  key={key}
                  data-testid={`plan-card-${key}`}
                  className={`rounded-2xl p-6 transition-all ${
                    isHighlight
                      ? 'border-2 bg-[#111827] relative'
                      : 'bg-[#111827] border border-white/5'
                  }`}
                  style={isHighlight ? {borderColor: GY_GREEN} : {}}
                >
                  {isHighlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold text-[#050a14]" style={{background: GY_GREEN}}>
                      {t('guyarepairLanding.popular')}
                    </div>
                  )}
                  <h3 className="text-sm font-bold uppercase tracking-wider" style={{color: isHighlight ? GY_GREEN : '#9ca3af'}}>{plan.name}</h3>
                  <div className="mt-3 mb-4">
                    <span className="text-3xl font-black text-white">{plan.price}</span>
                    <span className="text-sm text-gray-500 ml-1">{t('guyarepairLanding.perMonth')}</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {(plan.features || []).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-400">
                        <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{color: GY_GREEN}} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => { setSelectedPlan(key); setShowRegister(true); }}
                    data-testid={`plan-select-${key}`}
                    className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all ${
                      isHighlight
                        ? 'text-[#050a14] hover:opacity-90'
                        : 'border border-white/10 text-gray-300 hover:bg-white/5 hover:border-white/20'
                    }`}
                    style={isHighlight ? {background: `linear-gradient(135deg, ${GY_GREEN}, ${GY_YELLOW})`} : {}}
                  >
                    {t('guyarepairLanding.commencer')}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ Footer ═══ */}
      <footer className="border-t border-white/5 py-12 px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/guyarepair_logo.png" alt="GUYAREPAIR" className="w-7 h-7 rounded-md object-contain" />
            <span className="font-bold text-sm">GUYAREPAIR</span>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600">{t('guyarepairLanding.copyright', { year: new Date().getFullYear() })}</p>
            <p className="text-[10px] text-gray-700 mt-1">{t('guyarepairLanding.trademarkNotice')}</p>
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <button onClick={() => navigate('/guyarepair/connexion')} className="hover:text-white transition-colors">{t('guyarepairLanding.navConnexion')}</button>
            <a href="#pricing" className="hover:text-white transition-colors">{t('guyarepairLanding.navTarifs')}</a>
            <a href="#directory" className="hover:text-white transition-colors">{t('guyarepairLanding.navAnnuaire')}</a>
          </div>
        </div>
      </footer>

      {/* ═══ Register Modal ═══ */}
      {showRegister && <RegisterModal plan={selectedPlan} plans={plans} onClose={() => setShowRegister(false)} navigate={navigate} />}
    </div>
  );
};

/* ─────── Register Modal ─────── */
const RegisterModal = ({ plan, plans, onClose, navigate }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    company_name: '', contact_name: '', email: '', password: '', phone: '', city: '', address: '', plan, visible_in_directory: true
  });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => { setForm(f => ({ ...f, plan })); }, [plan]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) { setError(t('guyarepairLanding.passwordTooShort')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/public/register`, form);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('guyarepairLanding.registerErrorDefault'));
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-[#111827] border border-white/10 rounded-2xl w-full max-w-md p-8 text-center" onClick={e => e.stopPropagation()} data-testid="register-success">
          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{background: `${GY_GREEN}20`}}>
            <Check className="w-8 h-8" style={{color: GY_GREEN}} />
          </div>
          <h2 className="text-lg font-bold text-white mb-2">{t('guyarepairLanding.registerSuccessTitle')}</h2>
          <p className="text-sm text-gray-400 mb-2">{t('guyarepairLanding.successAccountCreated')} <strong className="text-white">{t('guyarepairLanding.successTrial')}</strong>.</p>
          <p className="text-xs text-gray-500 mb-6">{t('guyarepairLanding.successLoginHint')}</p>
          <button onClick={() => navigate('/guyarepair/connexion')} data-testid="go-to-login-btn" className="px-6 py-3 rounded-xl font-semibold text-sm text-[#050a14]" style={{background: `linear-gradient(135deg, ${GY_GREEN}, ${GY_YELLOW})`}}>
            {t('guyarepairLanding.seConnecter')}
          </button>
        </div>
      </div>
    );
  }

  const currentPlan = plans[form.plan];

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#111827] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="register-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div>
            <h2 className="text-base font-bold text-white">{t('guyarepairLanding.createAccountTitle')}</h2>
            {currentPlan && <p className="text-xs text-gray-500">{t('guyarepairLanding.planLine', { nom: currentPlan.name, prix: currentPlan.price })}</p>}
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/5"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400" data-testid="register-error">{error}</div>}

          <div>
            <label className="block text-xs text-gray-400 mb-1">{t('guyarepairLanding.planLabel')}</label>
            <div className="flex gap-2">
              {['start', 'pro', 'super'].map(k => (
                <button key={k} type="button" onClick={() => setForm(f => ({...f, plan: k}))}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold uppercase transition-all border ${form.plan === k ? 'text-[#050a14]' : 'border-white/10 text-gray-400 hover:border-white/20'}`}
                  style={form.plan === k ? {background: GY_GREEN, borderColor: GY_GREEN} : {}}
                  data-testid={`register-plan-${k}`}
                >{k}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-gray-400 mb-1">{t('guyarepairLanding.companyNameLabel')} *</label>
              <input required value={form.company_name} onChange={e => setForm(f => ({...f, company_name: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" data-testid="register-company-name" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('guyarepairLanding.contactNameLabel')} *</label>
              <input required value={form.contact_name} onChange={e => setForm(f => ({...f, contact_name: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" data-testid="register-contact-name" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('guyarepairLanding.phoneLabel')}</label>
              <input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('guyarepairLanding.emailLabel')} *</label>
              <input required type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" data-testid="register-email" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('guyarepairLanding.passwordLabel')} *</label>
              <div className="relative">
                <input required type={showPwd ? 'text' : 'password'} value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))} className="w-full px-3 py-2.5 pr-10 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#009b3a]/40" data-testid="register-password" />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500">{showPwd ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}</button>
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('guyarepairLanding.cityLabel')}</label>
              <input value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none" data-testid="register-city" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">{t('guyarepairLanding.addressLabel')}</label>
              <input value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} className="w-full px-3 py-2.5 bg-[#0a0f1c] border border-white/10 rounded-lg text-sm text-white focus:outline-none" />
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer" data-testid="register-directory-toggle">
            <input type="checkbox" checked={form.visible_in_directory} onChange={e => setForm(f => ({...f, visible_in_directory: e.target.checked}))} className="rounded border-gray-600" />
            <span className="text-xs text-gray-400">{t('guyarepairLanding.directoryToggle')}</span>
          </label>

          <button type="submit" disabled={loading} data-testid="register-submit-btn" className="w-full py-3 rounded-xl font-bold text-sm text-[#050a14] disabled:opacity-50 transition-all hover:opacity-90" style={{background: `linear-gradient(135deg, ${GY_GREEN}, ${GY_YELLOW})`}}>
            {loading ? t('guyarepairLanding.creating') : t('guyarepairLanding.createMyAccount')}
          </button>
          <p className="text-center text-xs text-gray-600">
            {t('guyarepairLanding.alreadyRegistered')} <button type="button" onClick={() => { window.location.href = '/guyarepair/connexion'; }} className="hover:text-white transition-colors" style={{color: GY_GREEN}}>{t('guyarepairLanding.seConnecter')}</button>
          </p>
        </form>
      </div>
    </div>
  );
};

export default GuyarepairLandingPage;
